import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './ConfirmDialog.css';

const VARIANT_DEFAULT_ICON = {
  danger: 'fas fa-exclamation-triangle',
  warning: 'fas fa-question-circle',
  info: 'fas fa-info-circle',
};

/**
 * SweetAlert tarzı onay diyaloğu (portal).
 */
const ConfirmDialog = ({
  open,
  onCancel,
  onConfirm,
  title = 'Onay',
  message = '',
  variant = 'danger',
  iconClass,
  confirmLabel = 'Tamam',
  cancelLabel = 'İptal',
  confirmButtonClassName,
  nestedOverlay,
}) => {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) setBusy(false);
  }, [open]);

  const resolvedIcon = iconClass || VARIANT_DEFAULT_ICON[variant] || VARIANT_DEFAULT_ICON.danger;

  const iconWrapClass = useMemo(
    () =>
      `confirm-dialog-icon-wrap confirm-dialog-icon--${variant === 'primary' ? 'info' : variant}`,
    [variant],
  );

  const defaultConfirmBtn =
    variant === 'info' || variant === 'primary' ? 'btn btn-primary' : 'btn danger-btn';
  const confirmBtnClass = confirmButtonClassName || defaultConfirmBtn;

  const handleConfirm = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
    } finally {
      setBusy(false);
    }
  }, [busy, onConfirm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (!busy) onCancel?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return createPortal(
    <div
      className={`modal-overlay confirm-dialog-overlay${nestedOverlay ? ' confirm-dialog-overlay--nested' : ''}`}
      role="presentation"
      onClick={() => !busy && onCancel?.()}
    >
      <div
        className="confirm-dialog-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={iconWrapClass} aria-hidden>
          <i className={resolvedIcon} />
        </div>
        <h2 id="confirm-dialog-title" className="confirm-dialog-title">
          {title}
        </h2>
        {message ? <p className="confirm-dialog-message">{message}</p> : null}
        <div className="confirm-dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={() => !busy && onCancel?.()} disabled={busy}>
            {cancelLabel}
          </button>
          <button type="button" className={confirmBtnClass} onClick={handleConfirm} disabled={busy}>
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden /> Bekleyin…
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ConfirmDialog;
